import type { TemplateDefinition } from "./types";

export const T05: TemplateDefinition = {
  id: "t05",
  module: 2,
  number: 5,
  name: "Fourth Amendment Field Decision Tool",
  stage: "STAGE 1 — FOUNDATION",
  pillars: "Sector-Sensitive Specialization + Value-Based Credentialing",
  whyExists:
    "The value of the LEP-C is not knowing what the Fourth Amendment says — any officer knows that. The value is applying it correctly in real time, documenting that application, and defending it legally.",
  architectureContext:
    "This template serves the Sector-Sensitive Specialization pillar. It converts Fourth Amendment doctrine into a reusable field decision tool — ensuring your searches and seizures are constitutionally defensible and properly documented.",
  sections: [
    {
      id: "step1",
      title: "Step 1 — Was There a Stop or Seizure?",
      variant: "steps",
      fields: [
        { id: "stop_standard", type: "textarea", label: "The standard I apply to determine if a stop or seizure occurred", placeholder: "Your standard for identifying a seizure..." },
        { id: "ras_facts", type: "textarea", label: "Reasonable articulable suspicion — the specific facts I must document", placeholder: "The facts you must articulate..." },
        { id: "consensual_vs_seizure", type: "textarea", label: "The distinction between a consensual encounter and a seizure as I apply it in the field", placeholder: "How you distinguish consensual encounters..." },
      ],
    },
    {
      id: "step2",
      title: "Step 2 — Was There a Search?",
      variant: "steps",
      fields: [
        { id: "privacy_interest", type: "textarea", label: "How I determine whether a privacy interest was implicated", placeholder: "Your test for privacy interests..." },
        { id: "heightened_areas", type: "textarea", label: "Areas where a search requires heightened constitutional analysis in my experience", placeholder: "Areas requiring heightened analysis..." },
      ],
    },
    {
      id: "step3",
      title: "Step 3 — Basis for Search — Consent, Warrant, or Exception?",
      variant: "steps",
      fields: [
        { id: "consent_toggle", type: "toggle", label: "Consent (voluntary, uncoerced, documented how?)" },
        { id: "consent_how", type: "text", label: "How I apply consent searches", placeholder: "Your consent search protocol..." },
        { id: "incident_toggle", type: "toggle", label: "Search incident to arrest" },
        { id: "incident_how", type: "text", label: "How I apply search incident to arrest", placeholder: "Your SITA protocol..." },
        { id: "plain_view_toggle", type: "toggle", label: "Plain view doctrine" },
        { id: "plain_view_how", type: "text", label: "How I apply plain view", placeholder: "Your plain view protocol..." },
        { id: "auto_toggle", type: "toggle", label: "Automobile exception" },
        { id: "auto_how", type: "text", label: "How I apply the automobile exception", placeholder: "Your auto exception protocol..." },
        { id: "exigent_toggle", type: "toggle", label: "Exigent circumstances" },
        { id: "exigent_how", type: "text", label: "How I apply exigent circumstances", placeholder: "Your exigent circumstances protocol..." },
        { id: "inventory_toggle", type: "toggle", label: "Inventory search" },
        { id: "inventory_how", type: "text", label: "How I apply inventory searches", placeholder: "Your inventory search protocol..." },
        { id: "other_exception", type: "text", label: "Other (additional exceptions)", placeholder: "Document any additional exceptions..." },
      ],
    },
    {
      id: "step4",
      title: "Step 4 — Documentation of Constitutional Reasoning",
      variant: "steps",
      fields: [
        { id: "doc_language", type: "textarea", label: "How I document my constitutional analysis in the incident report — the specific language I use", placeholder: "The specific report language you use..." },
        { id: "doc_sequence", type: "textarea", label: "The sequence I follow to ensure my documentation is complete before closing the report", placeholder: "Your documentation sequence..." },
      ],
    },
  ],
};

export const T06: TemplateDefinition = {
  id: "t06",
  module: 2,
  number: 6,
  name: "Constitutional Compliance Checklist",
  stage: "STAGE 1 — FOUNDATION",
  pillars: "Sector-Sensitive Specialization + Value-Based Credentialing",
  whyExists:
    "Procedural errors during interrogation and detention become suppression motions. This checklist eliminates those errors before they happen.",
  architectureContext:
    "This template serves the Sector-Sensitive Specialization pillar. It converts constitutional procedure into a pre-flight checklist — ensuring no procedural step is missed during interrogation and detention.",
  sections: [
    {
      id: "s1",
      title: "Section 1 — Pre-Interrogation Checklist",
      checklist: {
        id: "pre_interrogation",
        label: "Pre-Interrogation",
        items: [
          { id: "custody_det", label: "Custody determination completed — document basis" },
          { id: "miranda_trigger", label: "Miranda trigger analysis — is subject in custody?" },
          { id: "miranda_read", label: "Miranda rights read verbatim from card or recording" },
          { id: "rights_doc", label: "Rights advisement documented with date, time, location" },
          { id: "voluntary_waiver", label: "Voluntary waiver confirmed — no coercion, threats, or promises" },
          { id: "waiver_doc", label: "Waiver documented — written or recorded" },
          { id: "subject_condition", label: "Subject's condition assessed — intoxication, mental state, age, language" },
          { id: "interpreter", label: "Interpreter arranged if needed and documented" },
        ],
      },
    },
    {
      id: "s2",
      title: "Section 2 — During Questioning Standards",
      checklist: {
        id: "during_questioning",
        label: "During Questioning",
        items: [
          { id: "invocation_monitor", label: "Invocation monitoring — listening for any statement that could constitute invocation of right to silence or counsel" },
          { id: "stopping_protocol", label: "Stopping protocol initiated immediately upon invocation" },
          { id: "questioning_halted", label: "Questioning halted — no further questions asked" },
          { id: "invocation_doc", label: "Invocation documented with exact words used by subject" },
          { id: "reinitiation", label: "Re-initiation standards applied if questioning resumes" },
          { id: "time_duration", label: "Time, duration, and breaks documented" },
        ],
      },
    },
    {
      id: "s3",
      title: "Section 3 — Statement Documentation",
      checklist: {
        id: "statement_doc",
        label: "Statement Documentation",
        items: [
          { id: "signed_waiver", label: "Signed waiver form obtained and attached to case file" },
          { id: "recording_init", label: "Recording initiated (if department policy requires)" },
          { id: "recording_labeled", label: "Recording labeled with case number, date, time, officer name" },
          { id: "chain_custody", label: "Chain of custody for recording initiated" },
          { id: "written_summary", label: "Written summary of oral statement obtained if not recorded" },
          { id: "spontaneous", label: "Any spontaneous statements documented separately with context" },
          { id: "codefendant", label: "Co-defendant separation documented if applicable" },
        ],
      },
      fields: [
        { id: "additional_notes", type: "textarea", label: "Notes — any additional documentation steps specific to your department policy", placeholder: "Department-specific documentation steps..." },
      ],
    },
  ],
};

export const T07: TemplateDefinition = {
  id: "t07",
  module: 2,
  number: 7,
  name: "Use-of-Force Decision Matrix",
  stage: "STAGE 1 — FOUNDATION",
  pillars: "Sector-Sensitive Specialization + Value-Based Credentialing",
  whyExists:
    "A Sentinel-certified officer does not rely on reaction alone. This Decision Matrix guides and defends every force decision and produces the documentation required at each level.",
  architectureContext:
    "This template serves the Value-Based Credentialing pillar. It structures force decisions around constitutional standards (Graham v. Connor) with mandatory documentation at each threat level — ensuring every force decision is defensible.",
  sections: [
    {
      id: "overview",
      title: "Constitutional Standard",
      fields: [
        { id: "overarching_standard", type: "text", label: "The overarching constitutional standard governing all my force decisions", placeholder: "Should reference Graham v. Connor objective reasonableness standard" },
        { id: "uf_policy_number", type: "text", label: "My department's use-of-force policy general order number", placeholder: "e.g., GO-308.02" },
      ],
    },
    {
      id: "matrix",
      title: "Force Decision Matrix",
      variant: "matrix",
      repeatable: {
        id: "force_matrix",
        label: "Threat Level",
        maxRows: 4,
        initialRows: 4,
        prepopulated: [
          { level: "Compliance", color: "green", behavior: "", response: "", standard: "", documentation: "", deescalation: "Available" },
          { level: "Resistance", color: "yellow", behavior: "", response: "", standard: "", documentation: "", deescalation: "Available" },
          { level: "Threat", color: "orange", behavior: "", response: "", standard: "", documentation: "", deescalation: "Available" },
          { level: "Deadly Threat", color: "red", behavior: "", response: "", standard: "", documentation: "", deescalation: "Not Available" },
        ],
        fields: [
          { id: "level", type: "text", label: "Threat Level", placeholder: "Threat level label" },
          { id: "color", type: "text", label: "Color Code", placeholder: "green/yellow/orange/red" },
          { id: "behavior", type: "textarea", label: "Subject Behavior indicators" },
          { id: "response", type: "textarea", label: "Authorized Response Options" },
          { id: "standard", type: "text", label: "Constitutional Standard that applies" },
          { id: "documentation", type: "textarea", label: "Documentation required" },
          { id: "deescalation", type: "select", label: "De-escalation opportunity", options: [{ label: "Available", value: "Available" }, { label: "Not Available", value: "Not Available" }] },
        ],
      },
    },
    {
      id: "post_force",
      title: "Post-Force Reporting Obligations",
      fields: [
        { id: "reports_required", type: "textarea", label: "Reports I must complete after any use-of-force incident", placeholder: "List required reports..." },
        { id: "time_deadline", type: "text", label: "The time deadline for submitting use-of-force documentation", placeholder: "e.g., Before end of shift" },
        { id: "supervisor_notify", type: "text", label: "The supervisor I notify immediately following any use of force", placeholder: "e.g., Shift Sergeant" },
        { id: "bodycam_reqs", type: "textarea", label: "Body camera requirements for use-of-force incidents in my department", placeholder: "Your department's body camera requirements..." },
      ],
    },
  ],
};

export const T08: TemplateDefinition = {
  id: "t08",
  module: 2,
  number: 8,
  name: "Personal Liability Risk Assessment Framework",
  stage: "STAGE 1 — FOUNDATION",
  pillars: "Sector-Sensitive Specialization + Value-Based Credentialing",
  whyExists:
    "Patrol officers are the most frequently named defendants in Section 1983 suits. A certified officer understands their personal exposure and has a documented framework to reduce it.",
  architectureContext:
    "This template serves the Value-Based Credentialing pillar. It makes personal liability exposure visible, documented, and actively managed — a professional competency that non-certified officers lack.",
  sections: [
    {
      id: "s1",
      title: "Section 1 — Risk Category Identification",
      description: "5 pre-defined risk categories. Set your risk rating based on your assignment.",
      repeatable: {
        id: "risk_categories",
        label: "Risk Category",
        maxRows: 8,
        initialRows: 5,
        prepopulated: [
          { name: "Use of Force", rating: "Medium", notes: "" },
          { name: "Search and Seizure", rating: "Medium", notes: "" },
          { name: "Failure to Act / Failure to Intervene", rating: "Low", notes: "" },
          { name: "Discrimination or Bias Claims", rating: "Low", notes: "" },
          { name: "Vehicle Pursuits", rating: "Medium", notes: "" },
        ],
        fields: [
          { id: "name", type: "text", label: "Risk Category", placeholder: "Category name" },
          { id: "rating", type: "select", label: "Risk Rating", options: [{ label: "Low", value: "Low" }, { label: "Medium", value: "Medium" }, { label: "High", value: "High" }, { label: "Critical", value: "Critical" }] },
          { id: "notes", type: "textarea", label: "Notes", placeholder: "Assignment-specific notes..." },
        ],
      },
    },
    {
      id: "s2",
      title: "Section 2 — Personal Exposure Assessment",
      fields: [
        { id: "top3_risks", type: "textarea", label: "My top 3 highest personal liability risk categories based on my current assignment and why", placeholder: "Your top 3 risks and rationale..." },
        { id: "near_misses", type: "textarea", label: "Recent incidents or near-misses that indicate elevated personal liability exposure", placeholder: "Recent incidents or near-misses..." },
        { id: "exposure_factors", type: "textarea", label: "Factors in my current assignment that increase my exposure", placeholder: "Assignment-specific exposure factors..." },
      ],
    },
    {
      id: "s3",
      title: "Section 3 — Mitigation Actions",
      repeatable: {
        id: "mitigation_rows",
        label: "Mitigation Action",
        maxRows: 10,
        initialRows: 1,
        addLabel: "+ Add Mitigation Action",
        fields: [
          { id: "category", type: "text", label: "Risk Category", placeholder: "From Section 1" },
          { id: "action", type: "text", label: "Specific mitigation action", placeholder: "Mitigation action..." },
          { id: "documentation", type: "textarea", label: "Documentation practice that reduces exposure", placeholder: "How documentation reduces exposure..." },
          { id: "training", type: "text", label: "Training or certification that addresses this risk", placeholder: "Relevant training..." },
          { id: "status", type: "select", label: "Status", options: [{ label: "Planned", value: "Planned" }, { label: "Active", value: "Active" }, { label: "Complete", value: "Complete" }] },
        ],
      },
    },
    {
      id: "s4",
      title: "Section 4 — Indemnification Awareness",
      fields: [
        { id: "dept_covers", type: "textarea", label: "What my department covers through indemnification", placeholder: "What your department covers..." },
        { id: "not_covered", type: "textarea", label: "What is NOT covered and falls on me personally", placeholder: "Your personal exposure..." },
        { id: "insurance_status", type: "text", label: "My personal liability insurance status", placeholder: "e.g., FOP legal defense plan, personal policy" },
        { id: "union_resources", type: "textarea", label: "The union or association resources available to me in a civil action", placeholder: "Available union/association resources..." },
      ],
    },
  ],
};
